import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Table,
  Box,
  SpaceBetween,
  Pagination,
  TextFilter,
  CollectionPreferences,
  Header,
  Select
} from '@amzn/awsui-components-react/polaris';
import { getReleaseColumns } from './columns';
import { STATUS_OPTIONS } from './constants';
import PropTypes from 'prop-types';
import ElementErrorBoundary from '../../../components/ErrorBoundary/ElementErrorBoundary';
import { MetricsVisualization } from '../MetricsVisualization';
import { ErrorDisplay } from '../../../components/ErrorDisplay';
import { Alert } from "@amzn/awsui-components-react/polaris";
import { useParams } from 'react-router-dom';
import { getDomainIdentifier } from '../../../../utils/helpers';
import { useSdpdsReadOnlyOperation } from '../../../hooks/useSdpdsReadOnlyOperation';

const ReleaseFetcher = ({ payload, onSuccess, onError }) => {
  const { data, error } = useSdpdsReadOnlyOperation({
    operation_name: 'GetStackReleaseSoftwareConfigsByState',
    payload
  });

  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;

    if (error) {
      processedRef.current = true;
      onError(error);
    } else if (data?.sdpdsReadOnlyOperation) {
      processedRef.current = true;
      onSuccess(data.sdpdsReadOnlyOperation);
    }
  }, [data, error, onSuccess, onError]);

  return null;
};

export const SoftwareUpdateTrackerDetails = ({ stackName, isSDPDSMetric = false }) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filteringText, setFilteringText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('IN_PROGRESS');
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [fetchPayload, setFetchPayload] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);
  const chartRef = useRef(null);
  const { clientId, domainName } = useParams();
  const domainIdentifier = getDomainIdentifier(clientId, domainName);

  const statusOptions = STATUS_OPTIONS.map(option => ({
    value: option.value,
    label: option.text || option.value
  }));

  const scrollToChart = useCallback(() => {
    setIsScrolling(true);
    if (chartRef.current) {
      const elementPosition = chartRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 100;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      setTimeout(() => {
        setIsScrolling(false);
      }, 500);
    }
  }, []);

  const handleReleaseSelect = useCallback((releaseVersion) => {
    setSelectedRelease(releaseVersion);
    setTimeout(scrollToChart, 100);
  }, []);

  const getPayload = (status, token = null) => ({
    status,
    stackName,
    maxResults: 50,
    ...(token && { nextToken: token })
  });

  const handleFetchSuccess = (response) => {
    const newItems = response.stackReleaseSoftwareConfigList || [];
    setItems(prevItems => [...prevItems, ...newItems]);
    setIsLoading(false);

    if (response.nextToken) {
      setNextToken(response.nextToken);
      setFetchPayload(getPayload(selectedStatus, response.nextToken));
      setFetchKey(prev => prev + 1);
    } else {
      setNextToken(null);
      setFetchPayload(null);
    }

    setError(null);
  };

  const handleFetchError = (err) => {
    setError(err.message || 'An error occurred while fetching releases');
    setIsLoading(false);
    setFetchPayload(null);
  };

  const startFetch = (status) => {
    setIsLoading(true);
    setFetchPayload(getPayload(status));
    setFetchKey(prev => prev + 1);
  };

  const resetAndFetch = (status) => {
    setItems([]);
    setNextToken(null);
    setError(null);
    setCurrentPageIndex(1);
    setSelectedRelease(null);
    startFetch(status);
  };

  useEffect(() => {
    if (stackName) {
      resetAndFetch(selectedStatus);
    }
  }, [selectedStatus, stackName]);

  const getFilteredItems = () => {
    if (!filteringText) return items;

    const searchText = filteringText.toLowerCase();
    return items.filter(item =>
      item.releaseVersion?.toLowerCase().includes(searchText) ||
      item.stackName?.toLowerCase().includes(searchText) ||
      item.deploymentId?.toLowerCase().includes(searchText)
    );
  };

  const metricsComponent = useMemo(() => {
    return selectedRelease ? (
      <MetricsVisualization
        releaseVersion={selectedRelease}
        stackName={stackName}
        isSDPDSMetric={isSDPDSMetric}
        domainIdentifier={domainIdentifier}
      />
    ) : null;
  }, [selectedRelease, stackName, isSDPDSMetric, domainIdentifier]);

  if (error) {
    return <ErrorDisplay />;
  }

  const filteredItems = getFilteredItems();
  const startIndex = (currentPageIndex - 1) * pageSize;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + pageSize);

  return (
    <ElementErrorBoundary>
      {fetchPayload && (
        <ReleaseFetcher
          key={fetchKey}
          payload={fetchPayload}
          onSuccess={handleFetchSuccess}
          onError={handleFetchError}
        />
      )}

      <SpaceBetween size="l">
        <Alert
          type="info"
          statusIconAriaLabel="Info"
          header="Note"
          dismissible
        >
          Click on a release version to view release progress and cluster metrics for a release across the stack.
        </Alert>
        <Table
          columnDefinitions={getReleaseColumns(handleReleaseSelect)}
          items={paginatedItems}
          loading={isLoading}
          loadingText="Loading releases..."
          filter={
            <TextFilter
              filteringPlaceholder="Search releases..."
              filteringText={filteringText}
              onChange={({ detail }) => {
                setFilteringText(detail.filteringText);
                setCurrentPageIndex(1);
              }}
              countText={`${filteredItems.length} matches`}
            />
          }
          header={
            <Header
              counter={`(${filteredItems.length})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Select
                    selectedOption={statusOptions.find(option => option.value === selectedStatus)}
                    onChange={({ detail }) => {
                      setSelectedStatus(detail.selectedOption.value);
                      setCurrentPageIndex(1);
                      setNextToken(null);
                    }}
                    options={statusOptions}
                    placeholder="Select status"
                  />
                </SpaceBetween>
              }
            >
              Releases
            </Header>
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              onChange={({ detail }) => {
                setCurrentPageIndex(detail.currentPageIndex);
                if (
                  detail.currentPageIndex * pageSize > items.length &&
                  nextToken
                ) {
                  setFetchPayload(getPayload(selectedStatus, nextToken));
                  setFetchKey(prev => prev + 1);
                }
              }}
              pagesCount={Math.ceil(filteredItems.length / pageSize)}
              pageSize={pageSize}
            />
          }
          preferences={
            <CollectionPreferences
              title="Preferences"
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={{ pageSize }}
              pageSizePreference={{
                title: 'Page size',
                options: [
                  { value: 10, label: '10 releases' },
                  { value: 20, label: '20 releases' },
                  { value: 50, label: '50 releases' },
                ],
              }}
              onConfirm={({ detail }) => {
                setPageSize(detail.pageSize);
                setCurrentPageIndex(1);
              }}
            />
          }
          empty={
            <Box textAlign="center" color="inherit">
              <b>No {selectedStatus.toLowerCase().replace('_', ' ')} releases found</b>
            </Box>
          }
          variant="embedded"
          stickyHeader={false}
        />
        {selectedRelease && (
          <div ref={chartRef} id="chart-section">
            {metricsComponent}
          </div>
        )}
      </SpaceBetween>
    </ElementErrorBoundary>
  );
};

SoftwareUpdateTrackerDetails.propTypes = {
  stackName: PropTypes.string.isRequired,
  isSDPDSMetric: PropTypes.bool
};
